import React, { useEffect, useState } from 'react';
import { AlertCircle, RefreshCw, ArrowLeft, ShieldAlert, PhoneCall, HelpCircle } from 'lucide-react';

export default function FailPage({ onHomeClick, onRetryClick }) {
  const [details, setDetails] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const txnId = params.get('txnid') || params.get('mihpayid') || `PAYU_FAIL_${Date.now()}`;
    const amount = params.get('amount') || '500';
    const firstname = params.get('firstname') || params.get('name') || 'Applicant';
    const errorMsg = params.get('error_Message') || params.get('field9') || 'Payment was cancelled or failed at PayU gateway.';
    const currentDate = new Date().toLocaleString('en-IN', {
      dateStyle: 'full',
      timeStyle: 'medium'
    });

    setDetails({
      txnId,
      amount,
      name: firstname,
      errorMsg,
      date: currentDate
    });
  }, []);

  const handleGoHome = () => {
    if (onHomeClick) {
      onHomeClick();
    } else {
      window.location.href = '/';
    }
  };

  const handleRetry = () => {
    if (onRetryClick) {
      onRetryClick();
    } else {
      window.location.href = '/#enquiry-section';
    }
  };

  if (!details) {
    return (
      <div style={{ padding: '4rem', textAlign: 'center' }}>
        <h2>Loading Payment Details...</h2>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F8FAFC', padding: '2.5rem 1rem' }}>
      <div style={{ maxWidth: '640px', margin: '0 auto' }}>
        {/* Navigation */}
        <div style={{ marginBottom: '1.5rem' }}>
          <button
            onClick={handleGoHome}
            className="btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#FFFFFF' }}
          >
            <ArrowLeft size={18} /> Back to Homepage
          </button>
        </div>

        {/* Failure Card */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: '0 20px 45px rgba(239, 68, 68, 0.12)',
          border: '1px solid #FCA5A5'
        }}>
          {/* Card Header */}
          <div style={{
            backgroundColor: '#991B1B',
            color: '#FFFFFF',
            padding: '2rem 1.5rem',
            textAlign: 'center'
          }}>
            <div style={{
              width: '68px',
              height: '68px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem auto',
              border: '2px solid #FCA5A5'
            }}>
              <AlertCircle size={40} color="#FCA5A5" />
            </div>

            <h1 style={{ fontSize: '1.65rem', fontWeight: 800, marginBottom: '0.35rem', color: '#FFFFFF' }}>
              Payment Failed / Cancelled
            </h1>
            <p style={{ fontSize: '0.92rem', color: '#FECDD3', margin: 0 }}>
              Transaction could not be completed via PayU Gateway
            </p>
          </div>

          {/* Card Body */}
          <div style={{ padding: '2rem 1.75rem' }}>
            <p style={{ fontSize: '0.95rem', color: '#334155', lineHeight: 1.6, textAlign: 'center', marginBottom: '1.75rem' }}>
              Dear <strong>{details.name}</strong>, we were unable to process your application token payment of <strong>₹{details.amount}</strong>. No need to worry!
            </p>

            {/* Error / Status Box */}
            <div style={{
              backgroundColor: '#FEF2F2',
              borderRadius: '16px',
              padding: '1.25rem',
              border: '1px solid #FEE2E2',
              marginBottom: '1.75rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.65rem', fontSize: '0.85rem' }}>
                <span style={{ color: '#64748B', fontWeight: 600 }}>PayU Transaction Ref:</span>
                <strong style={{ color: '#991B1B', fontFamily: 'monospace' }}>{details.txnId}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.65rem', fontSize: '0.85rem' }}>
                <span style={{ color: '#64748B', fontWeight: 600 }}>Payment Status:</span>
                <span style={{
                  backgroundColor: '#FEE2E2',
                  color: '#991B1B',
                  fontWeight: 800,
                  padding: '0.15rem 0.6rem',
                  borderRadius: '6px',
                  fontSize: '0.8rem'
                }}>
                  FAILED / CANCELLED
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.65rem', fontSize: '0.85rem' }}>
                <span style={{ color: '#64748B', fontWeight: 600 }}>Amount:</span>
                <strong style={{ color: '#0F2537' }}>₹{details.amount}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                <span style={{ color: '#64748B', fontWeight: 600 }}>Timestamp:</span>
                <span style={{ color: '#475569' }}>{details.date}</span>
              </div>
            </div>

            {/* Refund & Security Notice */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem',
              backgroundColor: '#F8FAFC',
              borderRadius: '14px',
              padding: '1rem 1.25rem',
              border: '1px solid #E2E8F0',
              marginBottom: '2rem'
            }}>
              <ShieldAlert size={22} color="#DC2626" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.5 }}>
                <strong style={{ color: '#0F2537', display: 'block', marginBottom: '0.2rem' }}>Refund & Account Protection</strong>
                If any money was debited from your bank account or card, it will be automatically refunded by PayU to your original payment method within 3-5 working days.
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <button
                type="button"
                onClick={handleRetry}
                className="btn-primary"
                style={{
                  width: '100%',
                  padding: '1rem',
                  fontSize: '1.05rem',
                  fontWeight: 800,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  backgroundColor: '#FF6B00'
                }}
              >
                <RefreshCw size={18} /> Retry Payment Now
              </button>

              <button
                type="button"
                onClick={handleGoHome}
                className="btn-secondary"
                style={{
                  width: '100%',
                  padding: '0.9rem',
                  fontSize: '0.98rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.4rem'
                }}
              >
                <ArrowLeft size={16} /> Return to Homepage
              </button>
            </div>
          </div>

          {/* Footer Assistance */}
          <div style={{
            backgroundColor: '#F1F5F9',
            padding: '1rem 1.5rem',
            textAlign: 'center',
            fontSize: '0.82rem',
            color: '#64748B',
            borderTop: '1px solid #E2E8F0',
            display: 'flex',
            justify: 'center',
            alignItems: 'center',
            gap: '1.25rem',
            flexWrap: 'wrap'
          }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              <HelpCircle size={15} color="#FF6B00" /> Need Help?
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontWeight: 600, color: '#0F2537' }}>
              <PhoneCall size={14} color="#059669" /> Support: admin@formsadda.com
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
